import { useQuoteComplianceChecks } from 'components/hooks/quotes/useQuoteComplianceChecks'
import ComplianceChecks from 'components/quoting/quotes/compliance/ComplianceChecks'
import { TextLink, useBreakpoint } from 'slab'
import { cn, formatMoney } from 'utils'
import type { QuotingProspectDiscount } from 'utils/web_api.schemas'

import { useQuote } from '../QuoteProvider'
import { AdpDiscountBanner } from './AdpDiscountBanner'
import { QuoteDetailsItem } from './QuoteCostItem'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'

type QuoteCostDetailsProps = {
  discount?: QuotingProspectDiscount;
}

export function QuoteCostDetails ({ discount }: QuoteCostDetailsProps) {
  const {
    willEnrollEmployees,
    totalMonthlyPlatformFee,
    perEmployeeCost,
    complianceFee,
    employeePortionOfInsurance,
    employerPortionOfInsurance,
    isLoading,
  } = useSelfServeExplorerContext()

  const { breakpointIsAtLeast } = useBreakpoint()
  const isMobile = !breakpointIsAtLeast('md')

  const {
    employeesWithUnaffordablePlans,
    ratingAreasWithUnfairAllowances,
  } = useQuoteComplianceChecks()

  const { quote } = useQuote()

  const discountedPerEmployeeCost = discount ? perEmployeeCost * discount.rate : perEmployeeCost

  // Calculate discounted platform fee if discount is active
  const discountedPlatformFee = discount
    ? discountedPerEmployeeCost * willEnrollEmployees.length + complianceFee
    : totalMonthlyPlatformFee

  return (
    <div className={cn('space-y-3', discount && 'pb-4')}>
      <QuoteDetailsItem
        label='Estimated monthly health insurance costs'
        value={formatMoney(employerPortionOfInsurance)}
        isLoading={isLoading}
        tooltip="The estimated net amount you'll pay after employee contributions"
      />
      <QuoteDetailsItem
        label='Estimated monthly amount your employees will pay'
        value={formatMoney(employeePortionOfInsurance)}
        isLoading={isLoading}
        tooltip='The estimated amount your employees will contribute towards their health insurance'
      />
      <QuoteDetailsItem
        label='Monthly fees'
        value={formatMoney(totalMonthlyPlatformFee)}
        discountedValue={discount ? formatMoney(discountedPlatformFee) : undefined}
        isLoading={isLoading}
        tooltip={<>For more information on Thatch pricing, see our <TextLink color='inherit' href='https://thatch.com/pricing' rel='noopener noreferrer' target='_blank'>pricing page</TextLink></>}
      />
      <div className='ml-4 space-y-1'>
        <QuoteDetailsItem
          label='Per employee cost'
          value={formatMoney(perEmployeeCost)}
          discountedValue={discount ? formatMoney(discountedPerEmployeeCost) : undefined}
          suffix={` x ${willEnrollEmployees.length} ${willEnrollEmployees.length === 1 ? 'employee' : 'employees'}`}
          variant='secondary'
          isLoading={isLoading}
          tooltip='The estimated total that will be billed for per-employee platform fees (based on number of active employees)'
        />
        <QuoteDetailsItem
          label='Compliance fee'
          value={formatMoney(complianceFee)}
          variant='secondary'
          isLoading={isLoading}
          tooltip='Monthly fee for administering your healthcare benefits, including handling payments, managing reporting, administering your plan, and handling payroll deductions'
        />
      </div>
      {discount && (
        <div className='mt-4'>
          <AdpDiscountBanner
            discountPercentage={discount.rate * 100}
            deadlineDate={discount.deadline}
            discountEndDate={discount.end_date}
          />
        </div>
      )}
      {isMobile && (
        <div className='pt-4 border-t border-gray-200'>
          <h2 className='body-sm text-gray-500 mb-2'>Compliance checks</h2>
          <ComplianceChecks
            maxEmployeeContributionPercentage={quote?.max_employee_contribution_percentage}
            employeesWithUnaffordablePlans={employeesWithUnaffordablePlans}
            ratingAreasWithUnfairAllowances={ratingAreasWithUnfairAllowances}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  )
}
